import React, { FC, useState } from 'react'
import { Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { SoapRuleForm } from '@/apps/latest/components/molecules/SoapRuleForm'
import {
  SoapRuleTable,
  describeRule,
} from '@/apps/latest/components/portal/SoapRuleTable'
import {
  useSoapRulesQuery,
  useCreateSoapRuleMutation,
  useUpdateSoapRuleMutation,
  useDeleteSoapRuleMutation,
} from '@/features/internal/soap-rules/hooks/useSoapRulesQuery'
import { SoapRule } from '@/features/internal/soap-rules/types/soapRule'
import {
  Banner,
  DialogShell,
  NotionButton,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

type DialogMode = 'create' | 'edit' | 'delete' | null

/**
 * SOAPルール (v3)。フォーマット自動挿入 / 文字列置換の後継を1画面に統合。
 * 一覧は自然文で表示し、上から順 (作成順) に適用される。
 */
export const SoapRulesPage: FC = () => {
  const { data, isLoading: loading, error } = useSoapRulesQuery()
  const rules = data ?? []
  const createMutation = useCreateSoapRuleMutation()
  const updateMutation = useUpdateSoapRuleMutation()
  const deleteMutation = useDeleteSoapRuleMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<SoapRule | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreate = () => {
    setSelectedRule(null)
    setDialogMode('create')
  }

  const handleEdit = (rule: SoapRule) => {
    setSelectedRule(rule)
    setDialogMode('edit')
  }

  const handleDelete = (rule: SoapRule) => {
    setSelectedRule(rule)
    setDialogMode('delete')
  }

  const handleSubmitRule = async (
    ruleData: Omit<SoapRule, 'id' | 'created_at'>
  ) => {
    try {
      setIsSubmitting(true)
      if (dialogMode === 'create') {
        await createMutation.mutateAsync(ruleData)
      } else if (dialogMode === 'edit' && selectedRule?.id) {
        await updateMutation.mutateAsync({
          id: selectedRule.id,
          rule: ruleData,
        })
      }
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedRule?.id) return
    try {
      setIsSubmitting(true)
      await deleteMutation.mutateAsync(selectedRule.id)
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
    }
  }

  if (error) {
    return (
      <PageScaffold title="SOAPルール">
        <Banner severity="danger">{error.message}</Banner>
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="SOAPルール"
      meta={rules.length > 0 ? `全 ${rules.length} 件` : undefined}
      actions={
        <NotionButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          新規ルール
        </NotionButton>
      }
    >
      <SoapRuleTable
        rules={rules}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <DialogShell
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        title={dialogMode === 'create' ? 'ルール作成' : 'ルール編集'}
        maxWidth="sm"
        disabled={isSubmitting}
      >
        <SoapRuleForm
          rule={selectedRule || undefined}
          onSubmit={handleSubmitRule}
          onCancel={handleCloseDialog}
          isLoading={isSubmitting}
        />
      </DialogShell>

      <DialogShell
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        title="ルール削除確認"
        maxWidth="xs"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton
              variant="secondary"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              キャンセル
            </NotionButton>
            <NotionButton
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? '削除中…' : '削除'}
            </NotionButton>
          </>
        }
      >
        <Typography sx={PORTAL_TEXT.rowLabel}>
          「{selectedRule ? describeRule(selectedRule) : ''}」を削除しますか？
        </Typography>
        <Typography sx={{ ...PORTAL_TEXT.cellMeta, mt: 0.5 }}>
          この操作は取り消すことができません。
        </Typography>
      </DialogShell>
    </PageScaffold>
  )
}

export default SoapRulesPage
